import React, { useState, useEffect } from 'react';
import TestimonialsSlider from './TestimonialsSlider';
import VisitorCounter from './VisitorCounter';

const Pricing = ({ onPaymentSuccess, autoShowPayment = false }) => {
  const [email, setEmail] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  
  // Scroll to pricing section if autoShowPayment
  useEffect(() => {
    if (autoShowPayment) {
      setTimeout(() => {
        const element = document.getElementById('pricing');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [autoShowPayment]);

  const plan = {
    name: 'ObitAuthor Package',
    price: 9.99,
    features: [
      'Up to 5 Fully Generated Obituaries',
      'Use anytime - no expiration',
      'Professional formatting',
      'Print-ready format',
      'PDF download'
    ]
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setProcessing(false);
      return;
    }

    try {
      // Track abandoned cart (before checkout)
      try {
        await fetch('/api/abandoned-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
      } catch (e) {
        // Don't fail checkout if cart tracking fails
        console.error('Cart tracking error:', e);
      }

      // Create Stripe checkout session
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="pricing-section" id="pricing">
      <div className="container py-5">
        <div className="text-center mb-5">
          <h2 className="commercial-heading mb-3">We're Here to Help During This Difficult Time</h2>
          <p className="commercial-subheading">Let's Get This Written</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="pricing-card glass-card">
              {/* Limited Time Offer Badge */}
              <div className="text-center mb-3">
                <span className="badge" style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '20px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
                  animation: 'pulse 2s infinite'
                }}>
                  <i className="bi bi-clock me-2"></i>
                  Limited Time: $9.99 for 5 Obituaries
                </span>
              </div>

              {/* Satisfaction Guarantee */}
              <div className="text-center mb-3">
                <div style={{
                  background: 'rgba(34, 197, 94, 0.15)',
                  border: '2px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '12px',
                  padding: '0.75rem 1rem',
                  display: 'inline-block'
                }}>
                  <i className="bi bi-shield-check-fill me-2" style={{ color: '#22c55e' }}></i>
                  <strong style={{ color: '#000000' }}>Satisfaction Guarantee:</strong>
                  <span style={{ color: '#000000', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                    Not happy? We'll redo it for free!
                  </span>
                </div>
              </div>

              <VisitorCounter />

              <h3 className="plan-name text-center">{plan.name}</h3>
              <div className="plan-price text-center">
                <span className="currency">$</span>
                <span className="amount">{plan.price}</span>
              </div>
              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <i className="bi bi-check-circle-fill"></i>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {/* Purchase Process Info */}
              <div className="purchase-process-info mt-4 mb-4">
                <p className="process-description text-center" style={{ fontSize: '0.95rem', color: '#000000', lineHeight: '1.6' }}>
                  <i className="bi bi-info-circle me-2"></i>
                  After purchase, you'll receive 5 redemption codes via email. 
                  Enter a code when you're ready, answer a few questions about your loved one, 
                  and we'll instantly generate a professional obituary with a downloadable PDF.
                </p>
              </div>

              {/* Email Input and Payment Form */}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label" style={{ color: '#000000', fontWeight: '600' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={processing}
                    style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: '#000000',
                      borderRadius: '12px',
                      padding: '0.75rem 1rem'
                    }}
                  />
                  <small className="text-muted" style={{ color: '#000000', opacity: 0.7 }}>
                    Your 5 redemption codes will be sent to this email address
                  </small>
                </div>

                {error && (
                  <div className="alert alert-danger mb-3" role="alert">
                    {error}
                  </div>
                )}

                <div className="text-center">
                  <button 
                    type="submit"
                    className="btn btn-primary btn-lg w-100 pulsate-btn btn-with-arrow"
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Continue to Payment'}
                    {!processing && <i className="bi bi-chevron-right chevron-animate ms-2"></i>}
                  </button>
                </div>

                <p className="text-center mt-3 small text-muted" style={{ color: '#000000', opacity: 0.7 }}>
                  <i className="bi bi-lock-fill me-1"></i>
                  Secure payment powered by Stripe
                </p>
              </form>
            </div>
          </div>
        </div>
        
        {/* Testimonials Slider */}
        <div className="row justify-content-center mt-4">
          <div className="col-lg-8 col-md-10">
            <TestimonialsSlider />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
